/**
 * Change Tracker Redux Slice
 *
 * Tracks all changes to budget data for unified save logic.
 * NO DEFAULTS - explicit changes only.
 * Direct field access - no defensive programming.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface ChangeTrackerState {
  changes: Record<string, any>;
  modifiedFields: string[];  // Using array instead of Set for serialization
  originalData: any | null;
  hasChanges: boolean;
}

const initialState: ChangeTrackerState = {
  changes: {},
  modifiedFields: [],
  originalData: null,
  hasChanges: false
};

const changeTrackerSlice = createSlice({
  name: 'budgetChangeTracker',
  initialState,
  reducers: {
    /**
     * Track a field change
     * @param field - Field name that changed
     * @param value - New value (NO DEFAULTS)
     */
    trackChange: (state, action: PayloadAction<{ field: string; value: any }>) => {
      const { field, value } = action.payload;

      state.changes[field] = value;

      if (!state.modifiedFields.includes(field)) {
        state.modifiedFields.push(field);
      }

      state.hasChanges = true;
    },

    /**
     * Track multiple changes at once
     * Used when complex components update multiple fields
     */
    trackMultipleChanges: (state, action: PayloadAction<Record<string, any>>) => {
      const changes = action.payload;

      Object.entries(changes).forEach(([field, value]) => {
        state.changes[field] = value;
        if (!state.modifiedFields.includes(field)) {
          state.modifiedFields.push(field);
        }
      });

      state.hasChanges = true;
    },

    /**
     * Reset all changes
     * Called after successful save or on cancel
     */
    resetChanges: (state) => {
      state.changes = {};
      state.modifiedFields = [];
      state.hasChanges = false;
    },

    /**
     * Set original data for comparison
     * Called when budget is loaded
     */
    setOriginalData: (state, action: PayloadAction<any>) => {
      state.originalData = action.payload;
    },

    /**
     * Remove a specific field from changes
     * Used when reverting individual field changes
     */
    removeChange: (state, action: PayloadAction<string>) => {
      const field = action.payload;

      delete state.changes[field];
      state.modifiedFields = state.modifiedFields.filter(f => f !== field);
      state.hasChanges = state.modifiedFields.length > 0;
    }
  }
});

export const {
  trackChange,
  trackMultipleChanges,
  resetChanges,
  setOriginalData,
  removeChange
} = changeTrackerSlice.actions;

// Selectors - Accept any state with budgetChangeTracker
interface StateWithChangeTracker {
  budgetChangeTracker: ChangeTrackerState;
}

export const selectChanges = (state: StateWithChangeTracker) =>
  state.budgetChangeTracker.changes;

export const selectModifiedFields = (state: StateWithChangeTracker) =>
  state.budgetChangeTracker.modifiedFields;

export const selectHasChanges = (state: StateWithChangeTracker) => {
  return state.budgetChangeTracker.hasChanges;
};

export const selectOriginalData = (state: StateWithChangeTracker) =>
  state.budgetChangeTracker.originalData;

export default changeTrackerSlice.reducer;